import { parse } from "graphql";
import { print } from "graphql/language/printer";
import { rawRequest, ClientError } from "graphql-request";
import { z } from "zod";
import { unset } from "../lib/unset";
import { get } from "../lib/get";
import { set } from "../lib/set";

const SLEEP_AFTER_PAGE_MS = 1000;
const RETRY_AFTER_COEFFICIENT = 3; // Sleep triple the retry time to compensate for all the other requests that might be happening at the same time

const sleep = async (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Contentful can reply 400 with an empty body (e.g. a malformed id in
// `sys: {id_in: …}`), which surfaces as `{"status":400,"headers":{}}` and no
// usable detail. Dump everything we have before rethrowing so the failing
// query/variables are visible in the build log.
function logUnrecoverableClientError(ex: ClientError): void {
  console.error(
    "Unrecoverable Contentful GraphQL error",
    JSON.stringify(
      {
        status: ex.response?.status,
        errors: ex.response?.errors,
        query: ex.request?.query,
        variables: ex.request?.variables,
      },
      null,
      2,
    ),
  );
}

export type ContentfulRequestContext = {
  spaceId: string;
  apiKey: string;
  environment: string;
  preview?: boolean;
  contentTimeLimit?: number;
  carouselEntryLimit?: number;
  alwaysIncludedCarouselEntries?: string[];
  waitAfterPageFetch?: boolean;
};

export function configFromEnvironment(): ContentfulRequestContext {
  const spaceId = process.env.CONTENTFUL_SPACE_ID;
  if (spaceId === undefined) {
    console.error("no CONTENTFUL_SPACE_ID found in environment");
    process.exit(1);
  }

  const apiKey = process.env.CONTENTFUL_API_KEY;
  if (apiKey === undefined) {
    console.error("no CONTENTFUL_API_KEY found in environment");
    process.exit(1);
  }

  const environment = process.env.CONTENTFUL_ENVIRONMENT ?? "master";

  return { spaceId, apiKey, environment };
}

export const ContentfulGraphQLResponseSchema = z.object({
  data: z.record(
    z.string(),
    z.object({
      total: z.number().optional(),
      skip: z.number().optional(),
      items: z.array(z.unknown()),
    }),
  ),
});

export type ContentfulGraphQLResponse = z.infer<
  typeof ContentfulGraphQLResponseSchema
>;

const removeDuplicateFragments = (query: string): string => {
  const ast = parse(query);

  const seen = new Set<string>();

  const newDefinitions = ast.definitions.filter((def) => {
    if (def.kind !== "FragmentDefinition") {
      return true;
    }

    const id = `${def.name.value}-${def.typeCondition.name.value}`;

    const haveSeen = seen.has(id);

    seen.add(id);

    return !haveSeen;
  });

  const newAst = {
    ...ast,
    definitions: newDefinitions,
  };

  return print(newAst);
};

export async function doContentfulRequest(
  query: string,
  variables?: Record<string, unknown>,
  ctx?: ContentfulRequestContext,
): Promise<ContentfulGraphQLResponse> {
  const config = ctx !== undefined ? ctx : configFromEnvironment();

  const url = `https://graphql.contentful.com/content/v1/spaces/${config.spaceId}/environments/${config.environment}`;

  const ITEM_LIMIT = variables?.limit ?? 50;

  const paginationRequestHelper = async (
    skip: number,
  ): Promise<ContentfulGraphQLResponse> => {
    try {
      const params = {
        ...variables,
        preview: ctx?.preview ?? false,
        limit: ITEM_LIMIT,
        skip,
      };

      const sansDuplicated = removeDuplicateFragments(query);

      // Contentful rejects any request whose query + variables exceed 8192
      // bytes (QUERY_TOO_BIG). Preview builds push the NewsEntries query close
      // to that: `alwaysIncludedEntries` carries every CTA-target article id,
      // and draft entries add many more than a live build sees. Collapse all
      // insignificant whitespace to a single space and drop spaces around
      // punctuators — a name-preserving GraphQL minify that keeps us well under
      // the limit. Only whitespace is touched; string literals here are single
      // tokens (e.g. `locale: "en"`), so none contain runs to collapse.
      const minified = sansDuplicated
        .replace(/\s+/g, " ")
        .replace(/\s*([{}()[\]:,!])\s*/g, "$1")
        .trim();

      const result = await rawRequest(url, minified, params, {
        Authorization: `Bearer ${config.apiKey}`,
      });

      const parsed = ContentfulGraphQLResponseSchema.parse(result);

      return parsed;
    } catch (ex) {
      if (!(ex instanceof ClientError)) {
        throw ex;
      }

      // Error case: rate limit exceeded (HTTP 429 or GraphQL RATE_LIMIT_EXCEEDED)
      // https://www.contentful.com/developers/docs/references/content-delivery-api/#/introduction/api-rate-limits
      const codes = ex.response.errors?.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (e) => (e.extensions?.contentful as any)?.code,
      );

      const isHttpRateLimit = ex.response.status === 429;
      const isGraphqlRateLimit =
        codes?.some((c) => c === "RATE_LIMIT_EXCEEDED") ?? false;

      if (isHttpRateLimit || isGraphqlRateLimit) {
        const retryAfterSeconds =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (ex.response.headers as any)?.["x-contentful-ratelimit-reset"] ?? 1;

        console.log(
          `Rate limit exceeded, retrying after ${retryAfterSeconds * RETRY_AFTER_COEFFICIENT}s`,
        );

        await sleep(retryAfterSeconds * RETRY_AFTER_COEFFICIENT * 1000);

        return paginationRequestHelper(skip);
      }

      if (codes === undefined || codes.some((x) => x === undefined)) {
        logUnrecoverableClientError(ex);
        throw ex;
      }

      // If there are references to draft entries, we have to remove them based on the error response
      // See https://github.com/contentful/contentful.js/issues/418
      const unresolvableLinkErrors = codes.filter(
        (c) => c === "UNRESOLVABLE_LINK",
      );

      if (unresolvableLinkErrors.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = { ...(ex.response.data as any) };

        const errorPaths = ex.response.errors?.map((e) => e.path ?? []);

        if (errorPaths === undefined) throw ex;

        for (const errorPath of errorPaths) {
          const errorParentPath = errorPath.slice(0, -1);
          const existing = get(data, errorParentPath);

          if (Array.isArray(existing)) {
            const idxToRemove = errorPath[errorPath.length - 1];
            // Drop the invalid entry reference from the array.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const newArray = existing.slice(0, idxToRemove as any as number);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            set(ex.response.data as any, errorParentPath, newArray);
          } else {
            unset(ex.response.data, errorPath);
          }
        }

        const resp = ContentfulGraphQLResponseSchema.parse({
          data,
        });

        return resp;
      }

      logUnrecoverableClientError(ex);
      throw ex;
    }
  };

  const firstResult = await paginationRequestHelper(0);

  // Assumes the query returns a single object with a single key.
  const key = Object.keys(firstResult.data)[0];
  const entry = firstResult.data[key];
  const { total, items } = entry;

  // Without a `total` there's nothing to paginate; return the response as-is.
  if (!total) {
    return firstResult;
  }

  const itemsClone = [...items];

  while (itemsClone.length < total) {
    const paginatedResult = await paginationRequestHelper(itemsClone.length);
    const { items } = paginatedResult.data[key];
    itemsClone.push(...items);

    // Sleep between pages to stay under Contentful rate limits; skippable to
    // speed up static builds.
    if (config.waitAfterPageFetch ?? true) {
      await sleep(SLEEP_AFTER_PAGE_MS);
    }
  }

  return {
    data: {
      [key]: {
        total,
        items: itemsClone,
      },
    },
  };
}

/**
 * Parses a Contentful collection's `items` against a per-item schema.
 *
 * In production (preview disabled) we parse strictly: a broken *published*
 * entry should fail loudly so it gets noticed and fixed.
 *
 * In preview, drafts are frequently incomplete (missing thumbnails, titles,
 * or links to other unpublished drafts that `doContentfulRequest` strips out).
 * A single bad draft must not crash the whole preview, so we validate each
 * item independently and skip the invalid ones, logging which entry was
 * dropped and why.
 */
export function parseEntries<T extends z.ZodTypeAny>(
  itemSchema: T,
  items: unknown,
  ctx: ContentfulRequestContext | undefined,
  label: string,
): z.infer<T>[] {
  const rawItems = z.array(z.unknown()).parse(items ?? []);

  if (!ctx?.preview) {
    return z.array(itemSchema).parse(rawItems);
  }

  return rawItems.flatMap((item) => {
    const result = itemSchema.safeParse(item);
    if (result.success) {
      return [result.data as z.infer<T>];
    }

    const id = (item as { sys?: { id?: string } })?.sys?.id ?? "<unknown>";
    const issues = result.error.issues
      .map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`)
      .join("; ");
    console.warn(`[${label}] Skipping invalid preview entry ${id}: ${issues}`);

    return [];
  });
}

type EntryWithSortDate = {
  sortDate?: string | null;
  sys: {
    firstPublishedAt?: string | null;
  };
};

export function sortByDate<T extends EntryWithSortDate>(entries: T[]): T[] {
  return entries.sort((a, b) => {
    const aDate = a.sortDate ?? a.sys.firstPublishedAt;
    const bDate = b.sortDate ?? b.sys.firstPublishedAt;

    if (!aDate) {
      return -1;
    }

    if (!bDate) {
      return 1;
    }

    const aTime = new Date(aDate).getTime();
    const bTime = new Date(bDate).getTime();

    if (bTime < aTime) {
      return -1;
    }

    return 1;
  });
}

export function sortEntries<T extends EntryWithSortDate>(entries: T[]): T[] {
  const bySortDate = sortByDate<T>(entries);

  // Preview entries have firstPublishedAt set to null, so we sort them to be first
  const previewEntriesFirst = bySortDate.sort((a) => {
    if (a.sys.firstPublishedAt || a.sortDate) {
      return 0;
    }

    return -1;
  });

  return previewEntriesFirst;
}

export function subDays(dirtyDate: Date | string, amount: number): Date {
  const date = new Date(dirtyDate);

  if (!amount) {
    return date;
  }

  date.setDate(date.getDate() - amount);

  return date;
}
